import { fetchAuthSession } from 'aws-amplify/auth';
import axios from 'axios';
import { isMobile } from 'react-device-detect';
import { AppConfig } from '../../Config';
import { AppDispatch } from '../../store/store';
import { ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import userContextSlice from '../../common/slices/userContextSlice';
import { hydrateImpersonation } from '../../common/slices/impersonationSlice';
import { fetchUserAttributes, getCurrentUser } from '@aws-amplify/auth';
import { emptySplitApi } from '../../store/emptyApi';
import { organizationContextApi } from '../../common/api/organizationContextApi';
import { refreshAuthToken } from '../../common/utils/authUtils';
import { setUserItem, getUserItem } from '../../common/utils/userStorage';
import { featureApi } from '../../areas/settings/feature-flags/api/featureApi';
import featureSlice from '../../common/slices/featureSlice';
import { ACCOUNT_ROLE } from '../../common/selectors/auth';

const LEADERBOARD_LOGIN_SESSION_KEY = 'leaderboard_login_recorded';

function shouldRecordLeaderboardLogin(): boolean {
  return sessionStorage.getItem(LEADERBOARD_LOGIN_SESSION_KEY) !== 'true';
}

function markLeaderboardLoginRecorded(): void {
  try {
    sessionStorage.setItem(LEADERBOARD_LOGIN_SESSION_KEY, 'true');
  } catch {
    // Ignore sessionStorage failures in restricted environments.
  }
}

function canRecordLeaderboardLogin(accountRole?: number): boolean {
  return accountRole === ACCOUNT_ROLE.REGULAR_USER;
}

type TestModeSession = {
  tokens: {
    idToken: {
      toString: () => string;
    };
  };
};

type PostLoginCallbackOptions = {
  isMobileDevice?: boolean;
  testModeUser?: any;
  testModeAttributes?: any;
  testModeSession?: TestModeSession;
};

export function getInitialRoute({
  isMobileDevice,
  isTeamLeader,
  isOrgAdmin,
  isStaffOrSuperAdmin,
  hasLeadershipTeams = false,
}: {
  isMobileDevice: boolean;
  isTeamLeader: boolean;
  isOrgAdmin: boolean;
  isStaffOrSuperAdmin: boolean;
  hasLeadershipTeams?: boolean;
}): string {
  if (isMobileDevice) {
    if (isTeamLeader || (isOrgAdmin && hasLeadershipTeams)) {
      return '/app/mobile/culture-hub';
    }
    if (isOrgAdmin) {
      return '/app/mobile/org-admin';
    }
    if (isStaffOrSuperAdmin) {
      return '/app/mobile/staff';
    }
    return '/app/mobile';
  }
  if (isTeamLeader) {
    return '/app/culture-hub/analytics';
  }
  return '/app/dashboard';
}

async function recordLeaderboardLogin(organizationId: number | null, headers: any) {
  if (organizationId == null || !shouldRecordLeaderboardLogin()) {
    return;
  }

  try {
    const url = `${AppConfig.api.apiUrl}/api/portal/v1/leaderboard/login/`;
    await axios.post(
      url,
      { organization_id: organizationId },
      headers
    );
    markLeaderboardLoginRecorded();
  } catch (error) {
    console.warn('Failed to record leaderboard login event', error);
  }
}

// Wait for the feature slice to be marked as initialized in the Redux store.
// This helper dispatches a thunk that polls getState until the feature
// slice's `initialized` flag is true or a short timeout elapses. It avoids
// racing navigations that can cause a transient "Access Denied" flash.
async function waitForFeaturesInitialized(dispatch: any, timeoutMs = 2000) {
  return dispatch((_: any, getState: any) => {
    // Some test dispatch mocks only pass `dispatch` and not `getState`.
    // If `getState` is missing, resolve immediately to avoid breaking tests.
    if (typeof getState !== 'function') return Promise.resolve();

    if (getState().features?.initialized) {
      return Promise.resolve();
    }

    return new Promise((resolve) => {
      const start = Date.now();
      const interval = setInterval(() => {
        if (getState().features?.initialized) {
          clearInterval(interval);
          resolve(undefined);
          return;
        }
        if (Date.now() - start > timeoutMs) {
          // Timeout - resolve anyway to avoid blocking login flow
          clearInterval(interval);
          resolve(undefined);
        }
      }, 10);
    });
  });
}

export async function handleOrgChange(dispatch: AppDispatch, organization: any) {
  // Set transitioning state first
  dispatch(userContextSlice.actions.setTransitionState());

  try {
    // Fetch new organization context using RTK Query (with tags!)
    const response = await dispatch(
      organizationContextApi.endpoints.apiPortalV1OrganizationContextRetrieve.initiate({
        organizationId: organization.id
      })
    );

    if (response.error) {
      throw new Error('Failed to fetch organization context');
    }

    // Update localStorage
    if (organization?.id) {
      setUserItem('selected_org_id', organization.id.toString());
    }

    // Batch our state updates
    dispatch((dispatch: ThunkDispatch<any, any, UnknownAction>) => {
      // First initialize the new org context
      dispatch(userContextSlice.actions.initializeOrganizationContext(response.data));

      // Then reset API state to clear old org's data
      dispatch(emptySplitApi.util.resetApiState());
    });
  } catch (error) {
    // Handle error state
    dispatch(
      userContextSlice.actions.setError(
        error instanceof Error ? error.message : 'Failed to change organization'
      )
    );

    // Revert to previous organization if possible
    const previousOrgId = getUserItem('selected_org_id');
    if (previousOrgId) {
      const previousOrg = organization.organizations?.find(
        (org: any) => org.id === Number(previousOrgId)
      );
      if (previousOrg) {
        dispatch(userContextSlice.actions.setSelectedOrganization(previousOrg));
      }
    }
  }
}

export async function postLoginCallback(
  signOut: any,
  navigate: (path: string) => void,
  dispatch: any,
  isRefresh: boolean = false,
  options: PostLoginCallbackOptions = {}
) {
  const { isMobileDevice, testModeUser, testModeAttributes, testModeSession } = options;

  try {
    // In test mode, use provided data; otherwise get from Amplify
    let user, attributes, session;

    if (testModeUser && testModeAttributes && testModeSession) {
      // Test mode: use provided data
      user = testModeUser;
      attributes = testModeAttributes;
      session = testModeSession;
    } else {
      // Normal mode: get from Amplify
      user = await getCurrentUser();
      if (!user) {
        return;
      }
      attributes = await fetchUserAttributes();
      session = await fetchAuthSession();
    }

    if (!session.tokens?.idToken) {
      signOut?.();
      return;
    }

    const cognitoId = attributes.sub;
    const token = session.tokens.idToken.toString();
    localStorage.setItem('cognito_token', token);

    // In test mode, DRF tokens use "Token" prefix; JWTs use "Bearer"
    const isTestMode = process.env.REACT_APP_TEST_MODE === 'true';
    const tokenPrefix = isTestMode ? 'Token' : 'Bearer';
    
    const headers = {
      headers: {
        Authorization: `${tokenPrefix} ${token}`,
        'Content-Type': 'application/json',
      },
    };

    try {
      const response = await axios.get(AppConfig.api.apiUrl + '/api/portal/v1/context/', {
        ...headers,
        params: { cognitoId },
      });

      const data = response.data;
      const organizations = data.organizations;
      dispatch(
        userContextSlice.actions.initializeUserContext({
          idToken: token,
          user,
          organizations: data.organizations,
          departments: data.departments,
          account: data.account,
        })
      );
      dispatch(hydrateImpersonation(data.active_impersonation ?? null));

      if (organizations.length === 0) {
        throw new Error('User does not belong to any organizations');
      }

      // Determine which organization to select
      let selectedOrgId: number | null = null;

      if (organizations.length === 1) {
        // Single org - use it
        selectedOrgId = organizations[0].id;
      } else {
        // Multiple orgs - check for previously selected (user-specific)
        const previouslySelectedOrgId = getUserItem('selected_org_id');

        if (previouslySelectedOrgId) {
          const orgIndex = organizations.findIndex(
            (org: any) => Number(org.id) === Number(previouslySelectedOrgId)
          );

          if (orgIndex !== -1) {
            selectedOrgId = Number(previouslySelectedOrgId);
          } else {
            // No previously selected org or it's not valid - use first org
            selectedOrgId = Number(organizations[0].id);
          }
        } else {
          // No previously selected org - use first org
          selectedOrgId = Number(organizations[0].id);
        }
      }

      // Fetch full organization context for the selected org using RTK Query (with tags!)
      try {
        if (!selectedOrgId) {
          throw new Error('No organization ID selected');
        }

        const orgContextResponse = await dispatch(
          organizationContextApi.endpoints.apiPortalV1OrganizationContextRetrieve.initiate({
            organizationId: selectedOrgId
          })
        );

        if (orgContextResponse.error) {
          throw new Error('Failed to fetch organization context');
        }

        // Initialize organization context with full data
        dispatch(userContextSlice.actions.initializeOrganizationContext(orgContextResponse.data));

        if (canRecordLeaderboardLogin(data?.account?.role)) {
          void recordLeaderboardLogin(selectedOrgId, headers);
        }

        // Fetch features to determine if Culture Hub is enabled
        const featuresResponse = await dispatch(
          featureApi.endpoints.apiPortalV1FeaturesList.initiate(undefined)
        );
        
        if (featuresResponse.data) {
          dispatch(featureSlice.actions.setFeatures(featuresResponse.data));
          // Ensure feature slice initialization is observed by the rest of the app
          // before performing any navigation that depends on feature flags.
          await waitForFeaturesInitialized(dispatch);
        }

        // Navigate only if not refreshing
        if (!isRefresh) {
          const employeeAccountType = orgContextResponse.data?.employee?.accountType || orgContextResponse.data?.employee?.account_type;
          const isTeamLeader = employeeAccountType === 3;
          const isOrgAdmin = employeeAccountType === 4;
          const isStaffOrSuperAdmin =
            data?.account?.role === ACCOUNT_ROLE.STAFF ||
            data?.account?.role === ACCOUNT_ROLE.SUPERUSER;
          const leadershipTeams =
            orgContextResponse.data?.leadership_teams ||
            orgContextResponse.data?.leadershipTeams ||
            [];
          const hasLeadershipTeams =
            Array.isArray(leadershipTeams) && leadershipTeams.length > 0;
          navigate(
            getInitialRoute({
              isMobileDevice: isMobileDevice ?? isMobile,
              isTeamLeader,
              isOrgAdmin,
              isStaffOrSuperAdmin,
              hasLeadershipTeams,
            })
          );
        }
      } catch (orgError) {
        // Fallback: use the flat org data
        const selectedOrg = organizations.find((org: any) => org.id === selectedOrgId);
        if (selectedOrg) {
          dispatch(userContextSlice.actions.setSelectedOrganization(selectedOrg));

          if (canRecordLeaderboardLogin(data?.account?.role)) {
            void recordLeaderboardLogin(selectedOrgId, headers);
          }
          
          // Fetch features to determine if Culture Hub is enabled
          const featuresResponse = await dispatch(
            featureApi.endpoints.apiPortalV1FeaturesList.initiate(undefined)
          );
          
          if (featuresResponse.data) {
            dispatch(featureSlice.actions.setFeatures(featuresResponse.data));
            // Ensure that feature initialization is processed before navigating
            await waitForFeaturesInitialized(dispatch);
          }
          
          if (!isRefresh) {
            const employeeAccountType = data?.employee?.accountType || data?.employee?.account_type;
            const isTeamLeader = employeeAccountType === 3;
            const isOrgAdmin = employeeAccountType === 4;
            const isStaffOrSuperAdmin =
              data?.account?.role === ACCOUNT_ROLE.STAFF ||
              data?.account?.role === ACCOUNT_ROLE.SUPERUSER;
            const leadershipTeams =
              selectedOrg?.leadership_teams ||
              selectedOrg?.leadershipTeams ||
              data?.leadership_teams ||
              data?.leadershipTeams ||
              [];
            const hasLeadershipTeams =
              Array.isArray(leadershipTeams) && leadershipTeams.length > 0;
            
            navigate(
              getInitialRoute({
                isMobileDevice: isMobileDevice ?? isMobile,
                isTeamLeader,
                isOrgAdmin,
                isStaffOrSuperAdmin,
                hasLeadershipTeams,
              })
            );
          }
        }
      }
    } catch (error) {
      console.error('Error fetching user context:', error);

      // Handle 401 errors (unauthorized) - RTK Query handles token refresh for API calls
      // This is mainly for the initial axios call during login
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        // Extract the actual error message from the API response
        const errorMessage = error.response?.data?.detail || 
                            error.response?.data?.message || 
                            error.response?.data ||
                            'Unauthorized access';
        
        console.warn('Authentication error:', errorMessage);
        
        // Check if this is a user-blocked error (not a token issue)
        const isUserBlockedError = 
          typeof errorMessage === 'string' && (
            errorMessage.includes('no longer active') || 
            errorMessage.includes('do not have access to any organizations')
          );
        
        if (isUserBlockedError) {
          // User is blocked - dispatch to Redux and throw error to stop the flow
          dispatch(userContextSlice.actions.setAuthenticationError(errorMessage));
          // Just clear the local token
          localStorage.removeItem('cognito_token');
          // Throw a specific error that won't trigger retries
          throw new Error('USER_BLOCKED: ' + errorMessage);
        }
        
        // For other 401 errors, clear token, sign out and redirect to login
        localStorage.removeItem('cognito_token');
        signOut?.();
        navigate('/');
        return;
      }

      // Re-throw the error to be caught by the outer try-catch
      throw error;
    }
  } catch (error) {
    console.error('Authentication error:', error);

    // Check if this is a user-blocked error - don't try to refresh
    const errorString = error instanceof Error ? error.message : String(error);
    const isUserBlockedError = errorString.includes('USER_BLOCKED:');
    
    if (isUserBlockedError) {
      // User is blocked - just re-throw to let AuthenticatedApp handle it
      throw error;
    }

    // Handle token revocation - attempt refresh for initial setup errors
    const isTokenRevoked =
      errorString.includes('NotAuthorizedException') ||
      errorString.includes('Access Token has been revoked');

    if (isTokenRevoked) {
      const refreshedToken = await refreshAuthToken();
      if (refreshedToken) {
        // Retry the entire postLoginCallback with refreshed token
        try {
          await postLoginCallback(signOut, navigate, dispatch, true);
          return; // Success, exit early
        } catch (retryError) {
          console.error('Retry after token refresh failed:', retryError);
          // Fall through to sign out
        }
      }
      // If refresh failed, sign out
      console.warn('Token refresh failed, signing out user');
      localStorage.removeItem('cognito_token');
      signOut?.();
      navigate('/');
      return;
    }

    // For other errors, also sign out and redirect
    localStorage.removeItem('cognito_token');
    signOut?.();
    navigate('/');
  }
}

// Test mode wrapper that prepares Amplify-like data from JWT and calls postLoginCallback
export async function postLoginCallbackTestMode(
  signOut: any,
  navigate: (path: string) => void,
  dispatch: any,
  isRefresh: boolean = false
) {
  try {
    // Get token from localStorage (could be JWT or DRF Token)
    const tokenKey =
      localStorage.getItem('cognito_token') != null
        ? 'cognito_token'
        : localStorage.getItem('jwt_token') != null
        ? 'jwt_token'
        : localStorage.getItem('access_token') != null
        ? 'access_token'
        : null;

    const token =
      tokenKey === 'cognito_token'
        ? localStorage.getItem('cognito_token')
        : tokenKey === 'jwt_token'
        ? localStorage.getItem('jwt_token')
        : localStorage.getItem('access_token');

    if (!token) {
      console.error('No token found in test mode');
      signOut?.();
      return;
    }

    const tokenParts = token.split('.');
    const isJWT = tokenParts.length === 3;
    
    let email: string;
    let cognitoId: string;
    
    if (isJWT) {
      try {
        // Decode the JWT payload (second part)
        const payload = JSON.parse(atob(tokenParts[1]));
        email = payload.email;
        cognitoId = payload.sub;
      } catch (error) {
        console.error('Invalid JWT token format in test mode:', error);
        signOut?.();
        return;
      }

      if (!email || !cognitoId) {
        console.error('JWT payload missing required fields in test mode');
        signOut?.();
        return;
      }
    } else if (tokenKey === 'access_token') {
      // For DRF access tokens, use placeholder values.
      // The real postLoginCallback will fetch actual user context from the API.
      email = 'test@example.com';
      cognitoId = 'test-user';
    } else {
      console.error('Invalid JWT token format in test mode');
      signOut?.();
      return;
    }

    // Create Amplify-like data structures for the existing postLoginCallback
    const testModeUser = {
      username: email,
      userId: cognitoId,
      signInDetails: {
        loginId: email,
        authFlowType: 'USER_SRP_AUTH',
      },
    };

    const testModeAttributes = {
      sub: cognitoId,
      email: email,
      email_verified: true,
    };

    const testModeSession = {
      tokens: {
        idToken: {
          toString: () => token,
        },
      },
    };

    // Call the real postLoginCallback - it handles everything including fetching user context
    await postLoginCallback(signOut, navigate, dispatch, isRefresh, {
      isMobileDevice: isMobile,
      testModeUser,
      testModeAttributes,
      testModeSession,
    });
  } catch (error) {
    console.error('Error in test mode authentication:', error);
    signOut?.();
  }
}
